import { useState, useMemo } from "react";
import { Check, ChevronDown, Settings2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  STUDIO_PRESETS,
  STUDIO_ADVANCED_PRESET_ID,
  type StudioPreset,
} from "@/pages/GenerateProductCatalog/constants";

interface StudioPresetGridProps {
  /** Currently selected preset id, or `STUDIO_ADVANCED_PRESET_ID`, or null. */
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function StudioPresetGrid({ selectedId, onSelect }: StudioPresetGridProps) {
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);

  const featured = useMemo(() => STUDIO_PRESETS.filter((p) => p.featured), []);
  const more = useMemo(() => STUDIO_PRESETS.filter((p) => !p.featured), []);

  // Auto-reveal the secondary presets if the user already selected one of them
  // (e.g. via deep-link or a previous session). Keeps state consistent with
  // what's actually visible.
  const isMoreSelected = !!more.find((p) => p.id === selectedId);
  const expanded = showAll || isMoreSelected;

  const presetText = (preset: StudioPreset): { name: string; desc: string } => {
    const presets = t.productStudio.presets as Record<string, string>;
    return {
      name: presets[preset.nameKey] ?? preset.nameKey,
      desc: presets[preset.descKey] ?? "",
    };
  };

  const isAdvancedSelected = selectedId === STUDIO_ADVANCED_PRESET_ID;

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.productStudio.pickPreset}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t.productStudio.pickPresetHint}
        </p>
      </div>

      <div className="relative -mx-4 md:mx-0">
        <div className="flex md:grid md:grid-cols-3 gap-3 md:gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory px-4 md:px-0 pb-2 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {featured.map((preset) => (
            <PresetCard
              key={preset.id}
              preset={preset}
              isActive={selectedId === preset.id}
              onSelect={() => onSelect(preset.id)}
              text={presetText(preset)}
            />
          ))}

          {expanded &&
            more.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                isActive={selectedId === preset.id}
                onSelect={() => onSelect(preset.id)}
                text={presetText(preset)}
              />
            ))}
        </div>
        {/* Right-edge fade — signals more content beyond the viewport. Mobile only. */}
        <div className="pointer-events-none absolute top-0 bottom-2 end-0 w-10 bg-gradient-to-l rtl:bg-gradient-to-r from-background to-transparent md:hidden" />
      </div>

      {/* "More styles" toggle (hidden once auto-revealed) */}
      {!expanded && more.length > 0 && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            {t.productStudio.moreStyles}
          </button>
        </div>
      )}

      {/* Small "Custom" button at the bottom of the grid */}
      <div className="mt-4 flex justify-center">
        <button
          type="button"
          onClick={() => onSelect(STUDIO_ADVANCED_PRESET_ID)}
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs md:text-sm font-medium transition-all",
            isAdvancedSelected
              ? "border-brand bg-brand/10 text-brand"
              : "border-border bg-card text-muted-foreground hover:border-brand/40 hover:text-foreground",
          )}
        >
          <Settings2 className="w-3.5 h-3.5" />
          {t.productStudio.advancedCard}
          {isAdvancedSelected && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
        </button>
      </div>
    </div>
  );
}

function PresetCard({
  preset,
  isActive,
  onSelect,
  text,
}: {
  preset: StudioPreset;
  isActive: boolean;
  onSelect: () => void;
  text: { name: string; desc: string };
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative aspect-[4/5] rounded-2xl overflow-hidden border-2 bg-card transition-all duration-200 text-start flex-shrink-0 w-[38vw] md:w-auto snap-start",
        isActive
          ? "border-brand ring-2 ring-brand shadow-md"
          : "border-border hover:border-brand/40 hover:shadow-sm",
      )}
    >
      {preset.previewImage ? (
        <img
          src={preset.previewImage}
          alt={text.name}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
          <Sparkles className="w-10 h-10 text-brand/30" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
        <span className="block text-sm font-semibold text-white">{text.name}</span>
        <span className="block text-[11px] text-white/75 mt-0.5 line-clamp-2">
          {text.desc}
        </span>
      </div>

      {isActive && (
        <div className="absolute top-2 end-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
          <Check className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}
